/**
 * Runtime configuration reader.
 *
 * Values resolve from `window.__ENV__`, which `docker/40-generate-env-js.sh` writes to
 * `/usr/share/nginx/html/env.js` at container start. That script is loaded as a blocking tag in
 * `<head>`, so it runs before the bundle and module-scope reads are safe.
 *
 * When `env.js` is absent — local `yarn start` and the legacy CapRover image — we fall back to
 * the value webpack inlined at build time, so both deployment paths work off one source tree.
 *
 * Adding a key means touching three places: this file, the ARG/ENV pairs in `Dockerfile.ecs`, and
 * the `RUNTIME_ENV_VARS` list in `docker/40-generate-env-js.sh`.
 */

export type RuntimeEnvKey =
  | 'DEFAULT_PATH'
  | 'GRAPHQL_ENDPOINT'
  | 'WEBSOCKET_URL'
  | 'CHARTS_BASE_URL'
  | 'CHARTS_SUBDOMAIN'
  | 'CHARTS_URL_PREFIX'
  | 'CACHE_POLICY'
  | 'FEATURE_ACTION_CABLED_ENABLED'
  | 'MUI_LICENSE_KEY'
  | 'TRUENTITY_INITIAL_VISIT_URL'
  | 'TRUENTITY_FOLLOWUP_VISIT_URL'
  | 'SMALL_DATA_GRID_PAGINATION_SIZE'
  | 'MEDIUM_DATA_GRID_PAGE_SIZE'
  | 'DEFAULT_DATA_GRID_PAGE_SIZE'
  | 'HIGH_DATA_GRID_PAGE_SIZE'
  | 'MAXIMUM_DATA_GRID_PAGE_SIZE';

type RuntimeEnv = Partial<Record<RuntimeEnvKey, string | undefined>>;

/**
 * Build-time fallbacks. Each read MUST be a static `process.env.X` literal — webpack only
 * substitutes literal member expressions, so a dynamic `process.env[key]` survives into the bundle
 * and throws `process is not defined` in the browser.
 *
 * dotenv-webpack substitutes only keys that are actually set at build time, so a key missing from
 * Dockerfile.ecs's ENV list would likewise survive as a real `process.env` access and throw on
 * load. The try/catch degrades that from a blank page to "runtime values only".
 */
const readBuildTimeEnv = (): RuntimeEnv => {
  try {
    return {
      DEFAULT_PATH: process.env.DEFAULT_PATH,
      GRAPHQL_ENDPOINT: process.env.GRAPHQL_ENDPOINT,
      WEBSOCKET_URL: process.env.WEBSOCKET_URL,
      CHARTS_BASE_URL: process.env.CHARTS_BASE_URL,
      CHARTS_SUBDOMAIN: process.env.CHARTS_SUBDOMAIN,
      CHARTS_URL_PREFIX: process.env.CHARTS_URL_PREFIX,
      CACHE_POLICY: process.env.CACHE_POLICY,
      FEATURE_ACTION_CABLED_ENABLED: process.env.FEATURE_ACTION_CABLED_ENABLED,
      MUI_LICENSE_KEY: process.env.MUI_LICENSE_KEY,
      TRUENTITY_INITIAL_VISIT_URL: process.env.TRUENTITY_INITIAL_VISIT_URL,
      TRUENTITY_FOLLOWUP_VISIT_URL: process.env.TRUENTITY_FOLLOWUP_VISIT_URL,
      SMALL_DATA_GRID_PAGINATION_SIZE: process.env.SMALL_DATA_GRID_PAGINATION_SIZE,
      MEDIUM_DATA_GRID_PAGE_SIZE: process.env.MEDIUM_DATA_GRID_PAGE_SIZE,
      DEFAULT_DATA_GRID_PAGE_SIZE: process.env.DEFAULT_DATA_GRID_PAGE_SIZE,
      HIGH_DATA_GRID_PAGE_SIZE: process.env.HIGH_DATA_GRID_PAGE_SIZE,
      MAXIMUM_DATA_GRID_PAGE_SIZE: process.env.MAXIMUM_DATA_GRID_PAGE_SIZE
    };
  } catch {
    return {};
  }
};

const BUILD_TIME_ENV = readBuildTimeEnv();

/** Runtime value injected by `env.js` if set, otherwise the value baked in at build time. */
export const readEnv = (key: RuntimeEnvKey): string | undefined => {
  // `window` is undefined under Jest's node environment; fall straight through to the build value.
  const runtimeValue = typeof window === 'undefined' ? undefined : window.__ENV__?.[key];
  return runtimeValue === undefined || runtimeValue === '' ? BUILD_TIME_ENV[key] : runtimeValue;
};

/** Numeric variant of {@link readEnv}; returns `fallback` when unset or non-numeric. */
export const readEnvInt = (key: RuntimeEnvKey, fallback: number): number => {
  const parsed = parseInt(readEnv(key) ?? '', 10);
  return Number.isNaN(parsed) ? fallback : parsed;
};
